import { useCallback, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';

import type { GetThreadByIdResponseDTO } from '../../../../../../../../../shared/api/magellan/assistant/dto';
import {
  generateResponseProjectBlockAnswerStream,
  getResponseProjectBlockThreadById,
} from '../../../../../../../../../shared/api/magellan/responseProjectBlock';
import { AssistantThreadMessageRole } from '../../../../../../../../../shared/entities/AssistantThreadMessage';
import type { AIChatContentSegment } from '../../../../../../assistant/types/aiChatContentSegment.types';

export function useGenerateAnswer() {
  const queryClient = useQueryClient();
  const controllerRef = useRef<AbortController | null>(null);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
    };
  }, []);

  const generateAnswer = useCallback(
    ({ blockId, threadId }: { blockId: number; threadId: number }) => {
      if (isPending || controllerRef.current) {
        return;
      }

      controllerRef.current = new AbortController();
      setIsPending(true);

      const queryKey = [
        getResponseProjectBlockThreadById.name,
        { blockId, threadId },
      ];

      generateResponseProjectBlockAnswerStream({
        threadId,
        blockId,
        signal: controllerRef.current.signal,
        onmessage(event) {
          if (!event.data) return;
          const segment = JSON.parse(event.data) as AIChatContentSegment;

          queryClient.setQueryData(
            queryKey,
            (old: GetThreadByIdResponseDTO) => {
              const lastMessage = old.messages.at(-1);
              let newMessage;
              if (lastMessage?.role === AssistantThreadMessageRole.ASSISTANT) {
                newMessage = {
                  ...lastMessage,
                  content: [...lastMessage.content, segment],
                };

                return {
                  ...old,
                  messages: [...old.messages.slice(0, -1), newMessage],
                };
              } else {
                newMessage = {
                  id: 0,
                  role: AssistantThreadMessageRole.ASSISTANT,
                  content: [segment],
                };
                return {
                  ...old,
                  messages: [...old.messages, newMessage],
                };
              }
            },
          );
        },
        onclose() {
          setIsPending(false);
          queryClient.invalidateQueries({ queryKey });
          controllerRef.current = null;
        },
        onerror(err) {
          setIsPending(false);
          Sentry.captureException(err);
          queryClient.removeQueries({ queryKey });
          controllerRef.current = null;
        },
      });
    },
    [queryClient, isPending],
  );

  return {
    generateAnswer,
    isPending,
  };
}
